import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { occurrenceService } from '../services/occurrence.service';
import { authService, cityService } from '../services/auth.service';
import { Occurrence, Stats, STATUS_LABELS, OccurrenceStatus, PRIORITY_LABELS, PRIORITY_COLORS, OccurrencePriority } from '../types';

import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

const STATUS_COLORS: Record<string, string> = {
  pending: '#f59e0b',
  in_progress: '#3b82f6',
  resolved: '#10b981',
  cancelled: '#ef4444',
  rejected: '#ef4444',
};

function markerIcon(occ: Occurrence) {
  const color = STATUS_COLORS[occ.status] || '#6b7280';
  const svgPin = occ.category?.pin;

  if (svgPin) {
    return L.divIcon({
      className: '',
      html: `<div style="color:${color};">${svgPin}</div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 34],
      popupAnchor: [0, -34],
    });
  }

  return L.divIcon({
    className: '',
    html: `<div style="width:24px;height:24px;background:${color};border:3px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  });
}

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [occurrences, setOccurrences] = useState<Occurrence[]>([]);
  const [stats, setStats] = useState<Stats>({ total: 0, pending: 0, inProgress: 0, resolved: 0 });
  const [city, setCity] = useState<any>(null);
  const [boundary, setBoundary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<Record<string, boolean>>({
    pending: true,
    in_progress: true,
    resolved: true,
    cancelled: true,
    rejected: true,
  });
  const [tab, setTab] = useState<'mine' | 'all'>('all');

  const activeCityId = user?.activeCityId || user?.primaryCityId || user?.cityId;

  useEffect(() => {
    loadData();
  }, [activeCityId]);

  const loadData = async () => {
    setLoading(true);
    setBoundary(null);
    setCity(null);
    try {
      const [occList, occStats] = await Promise.all([
        occurrenceService.findAll(),
        occurrenceService.getStats(),
      ]);
      setOccurrences(occList);
      setStats(occStats);

      if (activeCityId) {
        try {
          const [cityData, cityBoundary] = await Promise.all([
            cityService.getById(activeCityId),
            cityService.getBoundary(activeCityId).catch(() => null),
          ]);
          setCity(cityData);
          if (cityBoundary) setBoundary(cityBoundary);
        } catch {}
      }
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'in_progress': return 'status-in-progress';
      case 'resolved': return 'status-resolved';
      default: return 'status-cancelled';
    }
  };

  if (loading) return <div className="p-8">Carregando...</div>;

  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const tabOccurrences = tab === 'mine' ? occurrences.filter(o => o.citizenId === user?.id) : occurrences;

  const mapOccurrences = tabOccurrences.filter(o => {
    if (!statusFilter[o.status]) return false;
    if (o.status !== 'resolved') return true;
    if (o.resolvedAt) return new Date(o.resolvedAt) > thirtyDaysAgo;
    return false;
  });

  return (
    <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-yellow-600">Pendentes</p>
            <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-blue-600">Em andamento</p>
            <p className="text-3xl font-bold text-blue-600">{stats.inProgress}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-green-600">Resolvidos</p>
            <p className="text-3xl font-bold text-green-600">{stats.resolved}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-3 bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <h2 className="text-lg font-semibold">Ocorrências</h2>
                <div className="flex rounded-lg bg-gray-100 p-0.5 text-sm">
                  <button
                    onClick={() => setTab('mine')}
                    className={`px-3 py-1.5 rounded-md font-medium transition-colors ${tab === 'mine' ? 'bg-white shadow text-primary-700' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    Minhas
                  </button>
                  <button
                    onClick={() => setTab('all')}
                    className={`px-3 py-1.5 rounded-md font-medium transition-colors ${tab === 'all' ? 'bg-white shadow text-primary-700' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    Todas
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs flex-wrap">
                {user?.userType === 'admin' || user?.userType === 'manager' ? (
                  Object.entries(STATUS_COLORS).map(([key, hex]) => (
                    <label key={key} className="flex items-center gap-1 cursor-pointer select-none">
                      <input type="checkbox" checked={statusFilter[key]} onChange={() => setStatusFilter({ ...statusFilter, [key]: !statusFilter[key] })}
                        className="rounded border-gray-300" />
                      <span className="inline-block w-3 h-3 rounded-full" style={{backgroundColor: hex}}></span>
                      {STATUS_LABELS[key as OccurrenceStatus] || key}
                    </label>
                  ))
                ) : (
                  Object.entries(STATUS_COLORS).map(([key, hex]) => (
                    <span key={key} className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full" style={{backgroundColor: hex}}></span>{STATUS_LABELS[key as OccurrenceStatus] || key}</span>
                  ))
                )}
                <span className="text-gray-500">{mapOccurrences.length} no mapa</span>
              </div>
            </div>
            <div className="h-96 relative z-0 isolation-isolate">
              <MapContainer center={city ? [city.lat, city.lng] : [-26.9185, -49.0658]} zoom={13} className="h-full w-full">
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {boundary && (
                  <GeoJSON
                    key={activeCityId}
                    data={boundary}
                    style={{
                      color: '#3b82f6',
                      weight: 2,
                      opacity: 0.6,
                      fillColor: '#3b82f6',
                      fillOpacity: 0.08,
                    }}
                  />
                )}
                {mapOccurrences.map((occ) => (
                  <Marker key={occ.id} position={[occ.lat, occ.lng]} icon={markerIcon(occ)}>
                    <Popup>
                      <div className="text-sm max-w-56">
                        <p className="font-semibold">{occ.category?.name || occ.categoryId}</p>
                        <p className="text-gray-500">{STATUS_LABELS[occ.status]}</p>
                        {(user?.userType !== 'citizen' || occ.status !== OccurrenceStatus.PENDING) && (
                          <p className="text-xs mt-0.5">{PRIORITY_LABELS[occ.priority ?? OccurrencePriority.MEDIUM]}</p>
                        )}
                        {occ.description && (
                          <p className="mt-1.5 line-clamp-3 text-gray-700">{occ.description}</p>
                        )}
                        {occ.address && (
                          <p className="text-xs text-gray-500 mt-1">📍 {occ.address}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          {new Date(occ.createdAt).toLocaleString('pt-BR')}
                        </p>
                        {occ.images?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {occ.images.map((img, i) => (
                              <img key={i} src={img} alt="" className="w-16 h-16 object-cover rounded border" />
                            ))}
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>

          <div className="lg:col-span-1 bg-white rounded-lg shadow">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold">{tab === 'mine' ? 'Minhas Ocorrências' : 'Todas as Ocorrências'}</h2>
              <button onClick={() => navigate('/occurrences')} className="text-sm text-primary-600 hover:text-primary-700">
                Ver todas
              </button>
            </div>
            <div className="divide-y max-h-96 overflow-y-auto">
              {tabOccurrences.slice(0, 10).map((occ) => (
                <div key={occ.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/occurrences/${occ.id}`)}>
                    <div className="flex justify-between items-start">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{occ.category?.name || occ.categoryId}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {user?.userType !== 'citizen' && user?.userType !== 'priority' ? `${occ.citizen?.name || 'Anônimo'} · ` : ''}{new Date(occ.createdAt).toLocaleString('pt-BR')}
                        </p>
                        {occ.address && <p className="text-xs text-gray-400 truncate mt-0.5">{occ.address}</p>}
                      </div>
                      <div className="flex flex-col items-end gap-1 ml-2">
                        <span className={getStatusColor(occ.status)}>
                          {STATUS_LABELS[occ.status]}
                        </span>
                        {(user?.userType !== 'citizen' || occ.status !== OccurrenceStatus.PENDING) && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{ backgroundColor: PRIORITY_COLORS[occ.priority ?? OccurrencePriority.MEDIUM] + '20', color: PRIORITY_COLORS[occ.priority ?? OccurrencePriority.MEDIUM], border: `1px solid ${PRIORITY_COLORS[occ.priority ?? OccurrencePriority.MEDIUM]}` }}
                          >
                            {PRIORITY_LABELS[occ.priority ?? OccurrencePriority.MEDIUM]}
                          </span>
                        )}
                      </div>
                    </div>
                </div>
              ))}
            </div>
          </div>
        </div>
    </>
  );
}
